// src/components/organisms/PatientWorkspaceTabs/AnamnesisEditor.js
//
// Anamnez kayıt editörü. Master-detail layout'unda sağ panel olarak render edilir.
// Tüm autosave mantığı `useInlineAutosave` hook'undadır — bu bileşen saf form.
// Düz editör dili (kutusuz bölümler, drawer ile ekleme): bkz. _editorParts.

import React, { Suspense, lazy, useEffect, useMemo, useState, useCallback } from "react";
import { Box, IconButton, Typography, alpha } from "@mui/material";
import {
  DeleteOutline as DeleteIcon,
  MedicalServices as DiagnosisIcon,
  HistoryRounded as HistoryIcon,
  FamilyRestroomRounded as FamilyIcon,
} from "@mui/icons-material";

import FormTextField from "../../atoms/FormTextField";
import { useClinicModule } from "../../../hooks/useClinicModule";
import { useClinicTheme } from "../../../theme";
import { COLORS } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import {
  useCreateAnamnezMutation,
  useUpdateAnamnezMutation,
} from "../../../store/secretaryApi";
import { useListDiagnosesMutation } from "../../../store/systemVariablesApi";
import TagPickerDrawer from "./drawers/TagPickerDrawer";
import FamilyHistoryDrawer from "./drawers/FamilyHistoryDrawer";
import {
  SECTION_SX,
  SPLIT_GRID_SX,
  SPLIT_LEFT_SX,
  SPLIT_RIGHT_SX,
  listAreaSx,
  ITEM_ROW_SX,
  ITEM_NAME_SX,
  codeBadgeSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
  PdfButton,
} from "./_editorParts";
import useTerms from "../../../hooks/useTerms";

// Bkz. TreatmentEditor: @react-pdf ayrı chunk'ta kalsın diye tembel yüklenir.
const AnamnesisPreview = lazy(() => import("../pdf/AnamnesisPreview"));

const FAMILY_RELATIONSHIPS = [
  "Anne",
  "Baba",
  "Anneanne",
  "Babaanne",
  "Dede",
  "Eş",
  "Oğul",
  "Kız",
  "Kardeş",
  "Abi",
  "Abla",
  "Erkek Kardeş",
  "Kız Kardeş",
  "İkiz",
  "Üvey Anne",
  "Üvey Baba",
  "Üvey Kardeş",
  "Teyze",
  "Hala",
  "Amca",
  "Dayı",
  "Yeğen",
  "Kuzen",
  "Diğer",
];

// Seçim drawer'ında sunucudan sayfa başına gelen kayıt
const PICKER_PAGE_SIZE = 30;
const HISTORY_LIST_HEIGHT = 216;

const NOTE_FIELDS = [
  { key: "examinationNotes", label: "Muayene Notları" },
  { key: "examinationFindings", label: "Bulgular" },
  { key: "labNotes", label: "Lab Notları" },
  { key: "imagingNotes", label: "Görüntüleme Notları" },
  { key: "otherNotes", label: "Diğer Notlar" },
];

const anamnezFieldsFromRecord = (record) => ({
  diagnosis: record?.diagnosis || null,
  complaint: record?.complaint || "",
  medicalHistory: Array.isArray(record?.medicalHistory)
    ? record.medicalHistory
    : [],
  familyHistory: Array.isArray(record?.familyHistory)
    ? record.familyHistory
    : [],
  examinationNotes: record?.examinationNotes || "",
  examinationFindings: record?.examinationFindings || "",
  labNotes: record?.labNotes || "",
  imagingNotes: record?.imagingNotes || "",
  otherNotes: record?.otherNotes || "",
});

// Listeden gelen hastalık ({_id, code, name, category}) → kayıttaki tanı biçimi
const toDiagnosisRef = (d) => ({
  diagnosisDisease: d._id,
  diagnosisName: d.name || "",
  diagnosisCode: d.code || "",
  diagnosisCategory: (d.category && (d.category._id || d.category)) || null,
  diagnosisCategoryName: (d.category && (d.category.name || "")) || "",
});

const historyLabel = (m) =>
  typeof m === "string"
    ? m
    : m?.diagnosisName || m?.diagnosisCategoryName || m?.label || "—";

export default function AnamnesisEditor({
  record,
  patient,
  patientId,
  token,
  examinationId,
  canEdit,
  onCreated,
  onSaved,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
  onError,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  // Tetkik modülü kapalıysa lab/görüntüleme notları gösterilmez (spec §7.2).
  const investigationModule = useClinicModule("investigation");
  const noteFields = NOTE_FIELDS.filter(
    (f) => investigationModule || !["labNotes", "imagingNotes"].includes(f.key)
  ).map((f) => (f.key === "examinationNotes" ? { ...f, label: `${terms.exam} Notları` } : f));

  const [pdfOpen, setPdfOpen] = useState(false);
  const [createAnamnez] = useCreateAnamnezMutation();
  const [updateAnamnez] = useUpdateAnamnezMutation();
  const [listDiagnoses] = useListDiagnosesMutation();

  // Seçim drawer'ı: "diagnosis" | "history" | "family". Anahtar kapanırken
  // korunur ki kapanış animasyonunda içerik boşalmasın.
  const [picker, setPicker] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const openPicker = (key) => {
    setPicker(key);
    setPickerOpen(true);
  };
  const closePicker = () => setPickerOpen(false);

  // Server response: { status, data: { anamnez } }. Hook'a kayıt nesnesini
  // doğrudan veriyoruz ki res._id ile create dönüşünü algılayabilsin.
  const createFn = useCallback(
    async (payload) => {
      const res = await createAnamnez(payload).unwrap();
      return res?.data?.anamnez || res?.anamnez || res;
    },
    [createAnamnez]
  );
  const updateFn = useCallback(
    async (payload) => {
      const res = await updateAnamnez(payload).unwrap();
      return res?.data?.anamnez || res?.anamnez || res;
    },
    [updateAnamnez]
  );

  // Tüm modlarda otomatik kayıt: tanı seçilince yeni kayıt oluşur, mevcut
  // kayıt düzenlemesi debounce ile kaydedilir. Manuel "Kaydet" butonu yok.
  const { fieldValues, setField, saveStatus } = useInlineAutosave({
    record,
    createMutation: createFn,
    updateMutation: updateFn,
    fieldsFromRecord: anamnezFieldsFromRecord,
    shouldCreate: (form) =>
      Boolean(
        form.diagnosis &&
          (form.diagnosis.diagnosisDisease || form.diagnosis._id)
      ),
    createTriggerField: "diagnosis",
    createDebounceMs: 200,
    debounceMs: 500,
    manualCreate: false,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      diagnosis: form.diagnosis,
      complaint: form.complaint,
      medicalHistory: form.medicalHistory,
      familyHistory: form.familyHistory,
      examinationNotes: form.examinationNotes,
      examinationFindings: form.examinationFindings,
      labNotes: form.labNotes,
      imagingNotes: form.imagingNotes,
      otherNotes: form.otherNotes,
    }),
    recordIdField: "anamnezId",
    revertOnError: true,
    onCreated,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  // saveStatus'u parent'a ilet (muayene ekranında başlıkta gösterilir).
  useEffect(() => {
    if (typeof onSaveStatusChange === "function")
      onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const isDisabled = !canEdit;
  const isNew = !record?._id;
  const hasDiagnosis = Boolean(
    fieldValues.diagnosis?.diagnosisDisease || fieldValues.diagnosis?._id
  );
  // Tanı seçilmeden kayıt oluşmaz; diğer alanlar o zamana kadar kilitli
  const lockedNoDx = isDisabled || (isNew && !hasDiagnosis);

  // ── Seçenek kaynakları (sunucuda sayfalı arama) ──
  const makeDiagnosisFetch = useCallback(
    (valueOf) => async ({ query, page }) => {
      const res = await listDiagnoses({
        token,
        status: "active",
        query: query || undefined,
        page,
        limit: PICKER_PAGE_SIZE,
      }).unwrap();
      const items = (res?.data?.diagnoses || []).map((d) => ({
        key: String(d._id),
        value: valueOf(d),
        title: d.name,
        subtitle: d.code || undefined,
        raw: d,
      }));
      return { items, hasMore: page < (res?.data?.pagination?.pages || 0) };
    },
    [listDiagnoses, token]
  );
  // Tanı: id ile; tıbbi geçmiş / aile öyküsü: ad ile ("Ekli" kontrolü addan)
  const fetchDiagnosisById = useMemo(() => makeDiagnosisFetch((d) => String(d._id)), [makeDiagnosisFetch]);
  const fetchDiagnosisByName = useMemo(() => makeDiagnosisFetch((d) => d.name), [makeDiagnosisFetch]);

  // ── Handlers ──
  const selectDiagnosis = (options) => {
    const d = options[0]?.raw;
    if (d) setField("diagnosis", toDiagnosisRef(d));
    closePicker();
  };

  // medicalHistory: listeden seçilen tanı yapılandırılmış, serbest metin
  // yalnız diagnosisName ile saklanır.
  const addMedHistory = (options) => {
    const current = fieldValues.medicalHistory || [];
    const seen = new Set(current.map((m) => historyLabel(m).toLocaleLowerCase("tr-TR")));
    const next = [...current];
    options.forEach((o) => {
      const entry = o.custom ? { diagnosisName: o.value } : toDiagnosisRef(o.raw);
      const key = String(entry.diagnosisName || "").toLocaleLowerCase("tr-TR");
      if (!key || seen.has(key)) return;
      seen.add(key);
      // medicalHistory şemasında kod alanı yok
      delete entry.diagnosisCode;
      next.push(entry);
    });
    setField("medicalHistory", next);
    closePicker();
  };
  const removeMedHist = (idx) => {
    setField(
      "medicalHistory",
      (fieldValues.medicalHistory || []).filter((_, i) => i !== idx)
    );
  };

  // familyHistory backend schema'sı: { relationshipType, disease }.
  const addFamilyHist = (entry) => {
    if (!entry?.relationshipType || !entry?.disease) return;
    setField("familyHistory", [...(fieldValues.familyHistory || []), entry]);
    closePicker();
  };
  const removeFamilyHist = (idx) => {
    setField(
      "familyHistory",
      (fieldValues.familyHistory || []).filter((_, i) => i !== idx)
    );
  };

  const medHistory = fieldValues.medicalHistory || [];
  const familyHistory = fieldValues.familyHistory || [];
  const diagnosis = fieldValues.diagnosis;

  const removeButton = (label, onClick) =>
    canEdit ? (
      <IconButton
        size="small"
        aria-label={`${label} kaldır`}
        onClick={onClick}
        sx={{ color: COLORS.slate400, "&:hover": { color: COLORS.errorDark } }}
      >
        <DeleteIcon sx={{ fontSize: 16 }} />
      </IconButton>
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Yeni anamnez kaydı oluşturuluyor"
        editLabel="Anamnez kaydı düzenleniyor"
        saveStatus={saveStatus}
        palette={palette}
        actions={
          // Çıktı — salt-okunur kullanıcı da alabilmeli, canEdit'e bağlı değil
          !isNew ? (
            <PdfButton
              title="Anamnez çıktısı (PDF)"
              onClick={() => setPdfOpen(true)}
              palette={palette}
            />
          ) : null
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", position: "relative" }}>
        {/* Tanı (zorunlu) + Şikayet */}
        <Box sx={SECTION_SX}>
          <SectionHeader
            title="Tanı *"
            addLabel={hasDiagnosis ? "Değiştir" : "Tanı Seç"}
            addVariant={hasDiagnosis ? "edit" : "add"}
            onAdd={canEdit ? () => openPicker("diagnosis") : undefined}
            disabled={isDisabled}
            palette={palette}
          />
          {hasDiagnosis ? (
            <Box sx={{ ...ITEM_ROW_SX, borderTop: `1px solid ${COLORS.slate100}` }}>
              <Typography
                component="span"
                sx={codeBadgeSx(
                  diagnosis.diagnosisCode ? palette.primary : COLORS.slate300,
                  alpha(palette.primary, 0.08)
                )}
              >
                {diagnosis.diagnosisCode || "—"}
              </Typography>
              <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={diagnosis.diagnosisName}>
                {diagnosis.diagnosisName || "Tanı"}
              </Typography>
            </Box>
          ) : (
            <Box
              role="alert"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: COLORS.warningLight,
                border: `1px solid ${alpha(COLORS.warning, 0.4)}`,
              }}
            >
              <DiagnosisIcon sx={{ fontSize: 18, color: COLORS.warning }} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: COLORS.warningDarker }}>
                Tanı zorunludur. Tanı seçilince kayıt oluşur ve diğer alanlar açılır.
              </Typography>
            </Box>
          )}
          <FieldRow label="Şikayet">
            <FormTextField
              palette={palette}
              value={fieldValues.complaint || ""}
              onChange={(e) => setField("complaint", e.target.value)}
              disabled={lockedNoDx}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={`${terms.patientGen} şikayeti`}
            />
          </FieldRow>
        </Box>

        {/* Tıbbi Geçmiş | Aile Öyküsü — geniş ekranda yan yana */}
        <Box sx={SPLIT_GRID_SX}>
          <Box sx={SPLIT_LEFT_SX}>
            <SectionHeader
              title="Tıbbi Geçmiş"
              count={medHistory.length}
              addLabel="Ekle"
              onAdd={canEdit ? () => openPicker("history") : undefined}
              disabled={lockedNoDx}
              palette={palette}
            />
            <Box sx={listAreaSx(HISTORY_LIST_HEIGHT, medHistory.length === 0)}>
              {medHistory.length === 0 ? (
                <SectionEmpty
                  icon={<HistoryIcon />}
                  title="Tıbbi geçmiş eklenmedi"
                  hint={canEdit && !lockedNoDx ? "Geçirilmiş hastalıkları Ekle ile seçin." : undefined}
                />
              ) : (
                <Box>
                  {medHistory.map((m, idx) => {
                    const label = historyLabel(m);
                    return (
                      <Box key={`med-${idx}`} sx={ITEM_ROW_SX}>
                        <HistoryIcon sx={{ color: COLORS.slate500, fontSize: 18, flexShrink: 0 }} />
                        <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={label}>
                          {label}
                        </Typography>
                        {removeButton(label, () => removeMedHist(idx))}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={SPLIT_RIGHT_SX}>
            <SectionHeader
              title="Aile Öyküsü"
              count={familyHistory.length}
              addLabel="Ekle"
              onAdd={canEdit ? () => openPicker("family") : undefined}
              disabled={lockedNoDx}
              palette={palette}
            />
            <Box sx={listAreaSx(HISTORY_LIST_HEIGHT, familyHistory.length === 0)}>
              {familyHistory.length === 0 ? (
                <SectionEmpty
                  icon={<FamilyIcon />}
                  title="Aile öyküsü eklenmedi"
                  hint={canEdit && !lockedNoDx ? "Yakınlık ve hastalığı Ekle ile seçin." : undefined}
                />
              ) : (
                <Box>
                  {familyHistory.map((f, idx) => {
                    const rel = f.relationshipType || f.relationship || "";
                    const dis = f.disease || f.diagnosis || "";
                    return (
                      <Box key={`fam-${idx}`} sx={ITEM_ROW_SX}>
                        <Typography
                          component="span"
                          sx={codeBadgeSx(COLORS.slate600, COLORS.slate100)}
                        >
                          {rel || "—"}
                        </Typography>
                        <Typography sx={{ ...ITEM_NAME_SX, flex: 1 }} noWrap title={dis}>
                          {dis}
                        </Typography>
                        {removeButton(`${rel} ${dis}`, () => removeFamilyHist(idx))}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Notlar */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none" }}>
          <SectionHeader title="Notlar" palette={palette} />
          {noteFields.map((f) => (
            <FieldRow key={f.key} label={f.label}>
              <FormTextField
                palette={palette}
                value={fieldValues[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
                disabled={lockedNoDx}
                variant="standard"
                size="small"
                fullWidth
                multiline
                minRows={2}
              />
            </FieldRow>
          ))}
        </Box>
      </Box>

      <TagPickerDrawer
        open={pickerOpen && picker === "diagnosis"}
        onClose={closePicker}
        icon={<DiagnosisIcon />}
        title={hasDiagnosis ? "Tanıyı Değiştir" : "Tanı Seç"}
        subtitle="ICD listesinden tanı seçin"
        fetchPage={fetchDiagnosisById}
        existing={hasDiagnosis ? [String(diagnosis.diagnosisDisease || diagnosis._id)] : []}
        searchPlaceholder="Tanı adı veya kodu ara"
        noun="tanı"
        single
        onSubmit={(_, options) => selectDiagnosis(options)}
      />

      <TagPickerDrawer
        open={pickerOpen && picker === "history"}
        onClose={closePicker}
        icon={<HistoryIcon />}
        title="Tıbbi Geçmiş Ekle"
        subtitle="Geçirilmiş hastalıkları seçin"
        fetchPage={fetchDiagnosisByName}
        existing={medHistory.map(historyLabel)}
        searchPlaceholder="Hastalık ara"
        noun="hastalık"
        allowCustom
        onSubmit={(_, options) => addMedHistory(options)}
      />

      <FamilyHistoryDrawer
        open={pickerOpen && picker === "family"}
        onClose={closePicker}
        relationships={FAMILY_RELATIONSHIPS}
        fetchPage={fetchDiagnosisByName}
        onSubmit={addFamilyHist}
      />

      {pdfOpen && (
        <Suspense fallback={null}>
          <AnamnesisPreview
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            patient={patient}
            anamnez={{ ...(record || {}), ...fieldValues }}
          />
        </Suspense>
      )}
    </Box>
  );
}
